import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Swal from "sweetalert2";

const TaskList = () => {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const navigate = useNavigate();

  const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    setLoading(true);

    // Fetch staff list
    fetch(`${BASE_URL}staff`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 1) setStaffList(data.data);
      })
      .catch((err) => console.error("Error fetching staff list:", err));

    // Fetch tasks
    fetch(`${BASE_URL}tasks`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 1) {
          setTasks(data.data);
          setFilteredTasks(data.data);
        } else {
          setError("Failed to load tasks");
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setError("Error fetching tasks");
        console.error(err);
      });
  }, []);

  const getStaffName = (id) => {
    const staff = staffList.find((s) => s.id === id);
    return staff ? staff.name : "N/A";
  };

  // Main filter function - handles ALL filters
  const filterTasks = (term, start, end, status) => {
    const termLower = term.toLowerCase();
    const filtered = tasks.filter((task) => {
      // Search in both title and staff name
      const staffName = getStaffName(task.staff_id).toLowerCase();
      const title = task.title ? task.title.toLowerCase() : "";

      const matchesSearch =
        termLower === "" ||
        title.includes(termLower) ||
        staffName.includes(termLower);

      // Date filtering
      const taskDate = new Date(task.time);
      const taskDateOnly = new Date(
        taskDate.getFullYear(),
        taskDate.getMonth(),
        taskDate.getDate()
      );

      const startDateObj = start ? new Date(start) : null;
      const endDateObj = end ? new Date(end) : null;

      let matchesDateRange = true;

      if (startDateObj) {
        const startDateOnly = new Date(
          startDateObj.getFullYear(),
          startDateObj.getMonth(),
          startDateObj.getDate()
        );
        if (taskDateOnly < startDateOnly) {
          matchesDateRange = false;
        }
      }

      if (endDateObj) {
        const endDateOnly = new Date(
          endDateObj.getFullYear(),
          endDateObj.getMonth(),
          endDateObj.getDate()
        );
        if (taskDateOnly > endDateOnly) {
          matchesDateRange = false;
        }
      }

      // Status filtering
      const matchesStatus = status ? task.status === status : true;

      return matchesSearch && matchesDateRange && matchesStatus;
    });

    setFilteredTasks(filtered);
  };

  // Auto-filter for search term only
  useEffect(() => {
    if (tasks.length > 0) {
      filterTasks(searchTerm, "", "", statusFilter);
    }
  }, [searchTerm, statusFilter, tasks]); // Auto-filter when search or status changes

  // Handle search change - triggers auto-filter
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle status change - triggers auto-filter
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  // Date handlers - only update state, no auto-filter
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  // Apply Filter button - applies date filters
  const handleApplyFilter = () => {
    filterTasks(searchTerm, startDate, endDate, statusFilter);
  };

  const handleEdit = (id) => navigate(`/EditTask/${id}`);

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This task will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${BASE_URL}tasks/${id}`, { method: "DELETE" })
          .then((res) => res.json())
          .then((data) => {
            if (data.status === 1) {
              const updatedTasks = tasks.filter((t) => t.id !== id);
              setTasks(updatedTasks);
              setFilteredTasks(updatedTasks);
              Swal.fire(
                "Deleted!",
                "Task has been deleted successfully.",
                "success"
              );
            } else Swal.fire("Failed!", "Failed to delete task.", "error");
          })
          .catch(() => Swal.fire("Error!", "Something went wrong.", "error"));
      }
    });
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Task List", 14, 10);
    const tableData = filteredTasks.map((task) => [
      task.title || "N/A",
      task.description || "N/A",
      formatDate(task.time) || "N/A",
      task.status || "N/A",
      task.type || "N/A",
      getStaffName(task.staff_id),
    ]);
    doc.autoTable({
      head: [["Title", "Description", "Time", "Status", "Type", "Staff Name"]],
      body: tableData,
    });
    doc.save("task_list.pdf");
  };

  const openStatusModal = (id) => {
    setSelectedTaskId(id);
    setStatusModalOpen(true);
  };
  const closeStatusModal = () => setStatusModalOpen(false);

  const handleStatusChange = (status) => {
    fetch(`${BASE_URL}tasks/${selectedTaskId}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 1) {
          const updatedTasks = tasks.map((t) =>
            t.id === selectedTaskId ? { ...t, status: data.data.status } : t
          );
          setTasks(updatedTasks);
          setFilteredTasks(updatedTasks);
          closeStatusModal();
        } else alert("Failed to update task status");
      })
      .catch(() => alert("Error updating task status"));
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Task List</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row" id="basic-table">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-6"></div>
                  <div className="col-md-6 text-end">
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate("/AddTask")}
                    >
                      Fix
                    </button>
                    &nbsp;&nbsp;
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate("/AddTaskk")}
                    >
                      Variable
                    </button>
                    <button
                      className="btn btn-secondary ms-2"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="mb-3 row">
                  <div className="col-md-3">
                    <label htmlFor="searchTerm" className="form-label">
                      Search by Title or Staff Name
                    </label>
                    <input
                      type="text"
                      id="searchTerm"
                      className="form-control"
                      placeholder="Search task title or staff name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="statusFilter" className="form-label">
                      Status
                    </label>
                    <select
                      id="statusFilter"
                      className="form-control"
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                    >
                      <option value="">All</option>
                      <option value="Pending">Pending</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={handleStartDateChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={handleEndDateChange}
                    />
                  </div>

                  {/* Apply Filter button for dates */}
                  <div className="col-md-4 mt-4">
                    <button
                      className="btn btn-secondary me-2"
                      onClick={handleApplyFilter}
                    >
                      Apply Date Filter
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>Staff Name</th>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTasks.length > 0 ? (
                        filteredTasks.map((task) => (
                          <tr key={task.id}>
                            <td>{getStaffName(task.staff_id)}</td>
                            <td>{task.title || "N/A"}</td>
                            <td>{task.description || "N/A"}</td>
                            <td>{formatDate(task.time) || "N/A"}</td>
                            <td>{task.status || "N/A"}</td>
                            <td>{task.type || "N/A"}</td>
                            <td className="text-end">
                              <button
                                className="btn btn-success"
                                onClick={() => handleEdit(task.id)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-danger ms-2"
                                onClick={() => handleDelete(task.id)}
                              >
                                Delete
                              </button>
                              <button
                                className="btn btn-warning ms-2"
                                onClick={() => openStatusModal(task.id)}
                              >
                                Status
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No tasks found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {statusModalOpen && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
          aria-labelledby="statusModalLabel"
          aria-hidden="false"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="statusModalLabel">
                  Change Task Status
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeStatusModal}
                ></button>
              </div>
              <div className="modal-body text-center">
                <button
                  className="btn btn-success me-3"
                  onClick={() => handleStatusChange("Completed")}
                >
                  Completed
                </button>
                <button
                  className="btn btn-warning"
                  onClick={() => handleStatusChange("Pending")}
                >
                  Pending
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskList;
